import { Link, useRouterState } from '@tanstack/react-router';
import {
  Activity,
  ArrowUpCircle,
  Building2,
  Bug,
  CreditCard,
  FileText,
  Flag,
  KeyRound,
  LayoutDashboard,
  LogOut,
  Mail,
  Menu,
  Receipt,
  ScrollText,
  Users,
  X,
} from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';

import { useAdminBadges } from '@/shared/api/admin-overview';
import { api } from '@/shared/api/client';
import { useAuthStore } from '@/shared/auth/auth-store';
import { cn } from '@/shared/lib/cn';

/** Admin nav routes — narrowed to the strings TanStack router understands. */
type NavTarget =
  | '/'
  | '/users'
  | '/teams'
  | '/plans'
  | '/subscriptions'
  | '/upgrade-requests'
  | '/coupons'
  | '/flags'
  | '/managed-keys'
  | '/audit'
  | '/emails'
  | '/errors'
  | '/metrics';

interface NavItem {
  to: NavTarget;
  label: string;
  icon: ReactNode;
  badge?: 'pendingUpgradeRequests';
}

const NAV_GROUPS: Array<{ label: string | null; items: NavItem[] }> = [
  { label: null, items: [{ to: '/', label: 'Overview', icon: <LayoutDashboard size={16} /> }] },
  {
    label: 'Customers',
    items: [
      { to: '/users', label: 'Users', icon: <Users size={16} /> },
      { to: '/teams', label: 'Teams', icon: <Building2 size={16} /> },
    ],
  },
  {
    label: 'Billing',
    items: [
      {
        to: '/upgrade-requests',
        label: 'Upgrade requests',
        icon: <ArrowUpCircle size={16} />,
        badge: 'pendingUpgradeRequests',
      },
      { to: '/subscriptions', label: 'Subscriptions', icon: <Receipt size={16} /> },
      { to: '/plans', label: 'Plans', icon: <CreditCard size={16} /> },
      { to: '/coupons', label: 'Coupons', icon: <FileText size={16} /> },
    ],
  },
  {
    label: 'Platform',
    items: [
      { to: '/flags', label: 'Feature flags', icon: <Flag size={16} /> },
      { to: '/managed-keys', label: 'Managed AI keys', icon: <KeyRound size={16} /> },
      { to: '/metrics', label: 'Metrics', icon: <Activity size={16} /> },
    ],
  },
  {
    label: 'Logs',
    items: [
      { to: '/errors', label: 'Errors', icon: <Bug size={16} /> },
      { to: '/audit', label: 'Audit log', icon: <ScrollText size={16} /> },
      { to: '/emails', label: 'Email log', icon: <Mail size={16} /> },
    ],
  },
];

/**
 * Operator shell — grouped sidebar + main outlet.  Used by every admin page
 * (except `/login`).  Desktop (≥1024px): fixed sidebar.  Phone / tablet: a top
 * bar with a menu button that opens the same nav as a drawer.
 */
export function AdminLayout({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  // Close the drawer after navigating.
  useEffect(() => setOpen(false), [pathname]);

  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-100 lg:grid lg:grid-cols-[224px_minmax(0,1fr)]">
      {/* Phone / tablet top bar */}
      <div className="sticky top-0 z-30 flex items-center justify-between border-b border-neutral-800 bg-neutral-950/95 px-4 py-2.5 backdrop-blur lg:hidden">
        <Brand />
        <button
          type="button"
          aria-label={open ? 'Close menu' : 'Open menu'}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          className="relative rounded-md p-2 text-neutral-300 hover:bg-neutral-800"
        >
          {open ? <X size={18} /> : <Menu size={18} />}
          <PendingDot />
        </button>
      </div>

      {open ? (
        <div className="fixed inset-0 z-40 lg:hidden" role="dialog" aria-modal="true" aria-label="Navigation">
          <button aria-label="Close menu" className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-72 max-w-[85vw] overflow-y-auto border-r border-neutral-800 bg-neutral-950 px-3 py-4">
            <Sidebar />
          </aside>
        </div>
      ) : null}

      <aside className="sticky top-0 hidden h-screen overflow-y-auto border-r border-neutral-800 bg-neutral-950 px-3 py-4 lg:block">
        <Sidebar />
      </aside>

      <main className="min-w-0 px-4 py-5 sm:px-6 lg:px-8 lg:py-6">{children}</main>
    </div>
  );
}

function Brand() {
  return (
    <div className="flex items-center gap-2 px-1">
      <img src="/brand/pjorange.svg" alt="" aria-hidden className="size-7 rounded-md" draggable={false} />
      <div className="leading-tight">
        <div className="text-sm font-semibold text-orange-500">Projoint</div>
        <div className="text-[10px] uppercase tracking-wider text-neutral-500">Admin</div>
      </div>
    </div>
  );
}

function PendingDot() {
  const { data } = useAdminBadges();
  if (!data?.pendingUpgradeRequests) return null;
  return <span className="absolute right-1 top-1 size-2 rounded-full bg-amber-400" aria-hidden />;
}

function Sidebar() {
  const clear = useAuthStore((s) => s.clear);
  const { data: badges } = useAdminBadges();

  return (
    <div className="flex min-h-full flex-col">
      <div className="mb-5 px-2">
        <Brand />
      </div>
      <nav className="flex flex-col gap-3" aria-label="Admin">
        {NAV_GROUPS.map((g, gi) => (
          <div key={g.label ?? gi} className="flex flex-col gap-0.5">
            {g.label ? (
              <div className="px-3 pb-1 text-[10px] font-medium uppercase tracking-wider text-neutral-600">
                {g.label}
              </div>
            ) : null}
            {g.items.map((item) => {
              const count = item.badge ? (badges?.[item.badge] ?? 0) : 0;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  activeOptions={{ exact: item.to === '/' }}
                  className={cn(
                    'flex items-center gap-2 rounded-md px-3 py-1.5 text-sm text-neutral-300',
                    'hover:bg-neutral-800/60 hover:text-neutral-50',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500',
                    '[&.active]:bg-neutral-800 [&.active]:text-neutral-50',
                  )}
                  activeProps={{ className: 'active', 'aria-current': 'page' }}
                >
                  <span className="text-neutral-400">{item.icon}</span>
                  <span className="flex-1">{item.label}</span>
                  {count > 0 ? (
                    <span
                      className="rounded-full bg-amber-500/20 px-1.5 text-[11px] font-semibold tabular-nums text-amber-300 ring-1 ring-amber-500/40"
                      aria-label={`${count} pending`}
                    >
                      {count}
                    </span>
                  ) : null}
                </Link>
              );
            })}
          </div>
        ))}
      </nav>
      <button
        type="button"
        onClick={() => {
          // Revoke the session + clear the httpOnly admin cookie server-side,
          // then drop the in-memory token and bounce to /login. Navigate even
          // if the call fails — the client session is gone either way.
          void api('/api/auth/logout', { method: 'POST', body: {} })
            .catch(() => undefined)
            .finally(() => {
              clear();
              window.location.href = '/login';
            });
        }}
        className="mt-6 flex items-center gap-2 rounded-md px-3 py-2 text-left text-xs text-neutral-500 hover:bg-neutral-800/60 hover:text-neutral-200"
      >
        <LogOut size={13} /> Sign out
      </button>
    </div>
  );
}
